import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClassView = () => {
  const { classId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [overview, setOverview] = useState(null);
  const [userQuizzes, setUserQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchClassData();
    fetchUserQuizzes();
  }, [classId]);

  useEffect(() => {
    if (activeTab === 'students') fetchStudents();
    else if (activeTab === 'quizzes') fetchQuizzes();
    else if (activeTab === 'overview') fetchOverview();
  }, [activeTab]);

  const fetchClassData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes/my-classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cls = response.data.find(c => c.class_id === classId);
      setClassInfo(cls);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch class:', error);
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes/${classId}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes/${classId}/quizzes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizzes(response.data);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes/${classId}/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    }
  };

  const fetchUserQuizzes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quiz/history/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserQuizzes(response.data);
    } catch (error) {
      console.error('Failed to fetch user quizzes:', error);
    }
  };

  const handleAssignQuiz = async () => {
    if (!selectedQuiz) return;

    try {
      await axios.post(
        `${API_URL}/api/classes/${classId}/assign-quiz`,
        { 
          quiz_id: selectedQuiz,
          due_date: dueDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAssignModal(false);
      setSelectedQuiz('');
      setDueDate('');
      fetchQuizzes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign quiz');
    }
  };

  const copyJoinLink = () => {
    const link = `${window.location.origin}/classes/join/${classInfo.join_code}`;
    navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Class not found</h2>
          <button
            onClick={() => navigate('/classes')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Back to My Classes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/classes')}
            className="text-indigo-600 hover:text-indigo-800 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to My Classes
          </button>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{classInfo.name}</h1>
                {classInfo.subject && (
                  <p className="text-gray-600 mt-2">{classInfo.subject}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">Teacher: {classInfo.teacher_name}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600 mb-1">Join Code</div>
                <div className="text-2xl font-mono font-bold text-indigo-600">{classInfo.join_code}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('students')}
                className={`px-6 py-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'students'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Students
              </button>
              <button
                onClick={() => setActiveTab('quizzes')}
                className={`px-6 py-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'quizzes'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Quizzes
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && overview && (
              <div>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="text-3xl font-bold text-blue-600">{overview.total_students}</div>
                    <div className="text-sm text-gray-600 mt-1">Total Students</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="text-3xl font-bold text-green-600">{overview.total_quizzes}</div>
                    <div className="text-sm text-gray-600 mt-1">Total Quizzes Assigned</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-6">
                    <div className="text-3xl font-bold text-purple-600">{overview.average_class_score}%</div>
                    <div className="text-sm text-gray-600 mt-1">Average Class Score</div>
                  </div>
                </div>

                {overview.recent_activity && overview.recent_activity.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                    <div className="space-y-3">
                      {overview.recent_activity.map((activity, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium">{activity.student_name}</div>
                            <div className="text-sm text-gray-600">{activity.quiz_title}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-indigo-600">{activity.score}</div>
                            <div className="text-xs text-gray-500">{new Date(activity.completed_at).toLocaleDateString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-4xl mb-3">📊</div>
                    <p>Once students start taking quizzes, you'll see class insights here.</p>
                  </div>
                )}
              </div>
            )}

            {/* Students Tab */}
            {activeTab === 'students' && (
              <div>
                <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-900">Class Join Link</div>
                      <div className="text-sm text-gray-600 mt-1 font-mono">
                        {window.location.origin}/classes/join/{classInfo.join_code}
                      </div>
                    </div>
                    <button
                      onClick={copyJoinLink}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
                    >
                      {copySuccess ? '✓ Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>

                {students.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-4xl mb-3">👥</div>
                    <p>No students have joined this class yet. Share the join link below.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Student Name</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Quizzes Completed</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Average Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((student) => (
                          <tr key={student.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">{student.name}</td>
                            <td className="py-3 px-4">{student.quizzes_completed}</td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-indigo-600">{student.average_score}%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Quizzes Tab */}
            {activeTab === 'quizzes' && (
              <div>
                <div className="mb-6">
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-medium"
                  >
                    Assign Quiz
                  </button>
                </div>

                {quizzes.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-4xl mb-3">📝</div>
                    <p>No quizzes assigned yet. Assign a quiz to this class.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Quiz Title</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Assigned On</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Due Date</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Completed / Total</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Average Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quizzes.map((quiz) => (
                          <tr key={quiz.quiz_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{quiz.title}</td>
                            <td className="py-3 px-4">{new Date(quiz.assigned_at).toLocaleDateString()}</td>
                            <td className="py-3 px-4">
                              {quiz.due_date ? (
                                <span className={`px-2 py-1 rounded text-sm ${
                                  quiz.is_overdue 
                                    ? 'bg-red-100 text-red-700' 
                                    : 'bg-green-100 text-green-700'
                                }`}>
                                  {new Date(quiz.due_date).toLocaleDateString()}
                                  {quiz.is_overdue && ' (Overdue)'}
                                </span>
                              ) : (
                                <span className="text-gray-400">No due date</span>
                              )}
                            </td>
                            <td className="py-3 px-4">{quiz.completed_count} / {quiz.total_students}</td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-indigo-600">{quiz.average_score}%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Quiz Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Assign Quiz to Class</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select a quiz *
              </label>
              <select
                value={selectedQuiz}
                onChange={(e) => setSelectedQuiz(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">-- Select Quiz --</option>
                {userQuizzes.map((quiz) => (
                  <option key={quiz.quiz_id} value={quiz.quiz_id}>
                    {quiz.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date (optional)
              </label>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set a deadline for students to complete this quiz
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedQuiz('');
                  setDueDate('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignQuiz}
                disabled={!selectedQuiz}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassView;